"use client";

/**
 * Plays the finished video and downloads it. No editor, no upload — the frames
 * are drawn locally and encoded in the browser.
 *
 * Playback is driven by the narration audio element rather than a timer: the
 * audio is the one clock that can't drift from what gets muxed into the file.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Download, Loader2, Pause, Play, RotateCcw } from "lucide-react";
import {
	decodeNarration,
	downloadBlob,
	exportComparisonVideo,
	type VideoFormat,
} from "@/comparison/export-video";
import type { Scene } from "@/comparison/scene";
import type { ComparisonSpec, NarrationResult } from "@/comparison/types";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { SceneCanvas } from "./scene-canvas";

export function ResultView({
	scene,
	narration,
	spec,
	onStartOver,
}: {
	scene: Scene;
	narration: NarrationResult;
	spec: ComparisonSpec;
	onStartOver: () => void;
}) {
	const audioRef = useRef<HTMLAudioElement | null>(null);
	const frameRef = useRef<number | null>(null);
	const audioBufferRef = useRef<AudioBuffer | null>(null);
	const abortRef = useRef<AbortController | null>(null);

	const [timeMs, setTimeMs] = useState(0);
	const [playing, setPlaying] = useState(false);
	const [format, setFormat] = useState<VideoFormat>("mp4");
	const [exportProgress, setExportProgress] = useState<number | null>(null);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		const url = URL.createObjectURL(narration.audioBlob);
		const audio = new Audio(url);
		audio.preload = "auto";
		audioRef.current = audio;

		const onEnded = () => {
			setPlaying(false);
			setTimeMs(0);
			audio.currentTime = 0;
		};
		audio.addEventListener("ended", onEnded);

		return () => {
			audio.removeEventListener("ended", onEnded);
			audio.pause();
			audioRef.current = null;
			URL.revokeObjectURL(url);
		};
	}, [narration.audioBlob]);

	// Follow the audio clock while playing so the canvas and the sound agree.
	useEffect(() => {
		if (!playing) {
			if (frameRef.current) cancelAnimationFrame(frameRef.current);
			frameRef.current = null;
			return;
		}

		const tick = () => {
			const audio = audioRef.current;
			if (audio) setTimeMs(audio.currentTime * 1000);
			frameRef.current = requestAnimationFrame(tick);
		};
		frameRef.current = requestAnimationFrame(tick);

		return () => {
			if (frameRef.current) cancelAnimationFrame(frameRef.current);
			frameRef.current = null;
		};
	}, [playing]);

	useEffect(() => () => abortRef.current?.abort(), []);

	const togglePlay = useCallback(() => {
		const audio = audioRef.current;
		if (!audio) return;

		if (playing) {
			audio.pause();
			setPlaying(false);
			return;
		}
		// A rejected play() (autoplay policy, or a seek racing the decode) would
		// otherwise leave the button showing "pause" over silence.
		void audio
			.play()
			.then(() => setPlaying(true))
			.catch(() => setPlaying(false));
	}, [playing]);

	const seek = useCallback((nextMs: number) => {
		const audio = audioRef.current;
		if (audio) audio.currentTime = nextMs / 1000;
		setTimeMs(nextMs);
	}, []);

	const handleDownload = useCallback(async () => {
		setError(null);
		setExportProgress(0);

		const controller = new AbortController();
		abortRef.current = controller;

		try {
			audioRef.current?.pause();
			setPlaying(false);

			// Decoding is the slow part and the buffer never changes, so keep it.
			if (!audioBufferRef.current) {
				audioBufferRef.current = await decodeNarration({
					audioBlob: narration.audioBlob,
				});
			}

			const blob = await exportComparisonVideo({
				scene,
				audioBuffer: audioBufferRef.current,
				format,
				signal: controller.signal,
				onProgress: ({ progress }) => setExportProgress(progress),
			});

			downloadBlob({ blob, filename: filenameFor({ spec, format }) });
		} catch (caught) {
			if ((caught as DOMException)?.name === "AbortError") return;
			setError(
				caught instanceof Error ? caught.message : "Couldn't export the video.",
			);
		} finally {
			setExportProgress(null);
			abortRef.current = null;
		}
	}, [format, narration.audioBlob, scene, spec]);

	const exporting = exportProgress !== null;
	const durationMs = Math.max(1, narration.durationMs);

	return (
		<div className="mx-auto flex w-full max-w-5xl flex-col gap-6 lg:flex-row">
			<div className="mx-auto w-full max-w-sm lg:mx-0">
				<SceneCanvas scene={scene} timeMs={timeMs} />

				<div className="mt-3 flex items-center gap-3">
					<Button
						type="button"
						variant="outline"
						size="icon"
						aria-label={playing ? "Pause" : "Play"}
						onClick={togglePlay}
						disabled={exporting}
					>
						{playing ? <Pause className="size-4" /> : <Play className="size-4" />}
					</Button>
					<input
						type="range"
						min={0}
						max={durationMs}
						step={50}
						value={Math.min(timeMs, durationMs)}
						aria-label="Seek"
						disabled={exporting}
						onChange={(event) => seek(Number(event.target.value))}
						className="accent-primary h-1 flex-1 cursor-pointer"
					/>
					<span className="text-muted-foreground w-16 text-right text-xs tabular-nums">
						{formatTime({ ms: timeMs })} / {formatTime({ ms: durationMs })}
					</span>
				</div>
			</div>

			<div className="flex flex-1 flex-col gap-4">
				<div>
					<h2 className="text-xl font-semibold">Your video is ready</h2>
					<p className="text-muted-foreground mt-1 text-sm">
						Rendered entirely in this browser. Nothing was uploaded.
					</p>
				</div>

				<div className="border-border bg-card/40 rounded-xl border p-4">
					<Label>Format</Label>
					<div className="mt-2 flex flex-col gap-2 sm:flex-row">
						<Select
							value={format}
							onValueChange={(value) => setFormat(value as VideoFormat)}
							disabled={exporting}
						>
							<SelectTrigger className="w-full sm:w-40">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="mp4">MP4 (H.264)</SelectItem>
								<SelectItem value="webm">WebM (VP9)</SelectItem>
							</SelectContent>
						</Select>

						<Button
							type="button"
							onClick={() => void handleDownload()}
							disabled={exporting}
							className="h-9 flex-1 bg-[#4d2ef5] text-white hover:bg-[#3d1fe0]"
						>
							{exporting ? (
								<Loader2 className="size-4 animate-spin" />
							) : (
								<Download className="size-4" />
							)}
							{exporting
								? `Encoding ${Math.round((exportProgress ?? 0) * 100)}%`
								: "Download video"}
						</Button>
					</div>

					{exporting ? (
						<div className="bg-muted mt-3 h-1.5 w-full overflow-hidden rounded-full">
							<div
								className="bg-primary h-full transition-[width] duration-150"
								style={{ width: `${Math.round((exportProgress ?? 0) * 100)}%` }}
							/>
						</div>
					) : null}

					{error ? (
						<p className="text-destructive mt-2 text-xs">{error}</p>
					) : null}
				</div>

				<p className="text-muted-foreground text-xs">
					The pointing follows where the narrator says each name
					{narration.toolCuesUsed > 0
						? `, plus ${narration.toolCuesUsed} cue${narration.toolCuesUsed === 1 ? "" : "s"} the model set itself`
						: ""}
					. If a cue looks off, rename an option to exactly what you want spoken
					and generate again.
				</p>

				<details className="border-border bg-card/40 rounded-xl border p-4">
					<summary className="cursor-pointer text-sm font-medium">
						Transcript
					</summary>
					<p className="text-muted-foreground mt-2 text-sm leading-relaxed">
						{narration.transcript}
					</p>
				</details>

				<Button
					type="button"
					variant="outline"
					onClick={onStartOver}
					disabled={exporting}
					className="self-start"
				>
					<RotateCcw className="size-4" />
					Make another
				</Button>
			</div>
		</div>
	);
}

function formatTime({ ms }: { ms: number }): string {
	const total = Math.max(0, Math.round(ms / 1000));
	const minutes = Math.floor(total / 60);
	const seconds = total % 60;
	return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

function filenameFor({
	spec,
	format,
}: {
	spec: ComparisonSpec;
	format: VideoFormat;
}): string {
	const slug = `${spec.thisLabel}-vs-${spec.thatLabel}`
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, "-")
		.replace(/^-|-$/g, "")
		.slice(0, 60);
	return `${slug || "comparison"}.${format}`;
}
